import { useMemo } from 'react';

import { type UseImageSourceProps } from './useImageSourceProps.types';

/**
 * Returns component props object with the image source and optional static
 * image URL that sets `data` attribute on the image container which can be
 * used to swap animated image with a static version on web platform.
 *
 * NOTE: This allows our a11y vendor to swap animated image with a static
 *       static version on web.
 *
 * NOTE: On native platforms we decide the source based on the "reduce motion"
 *       mode.
 *
 * @example
 *
 * const imageSourceProps = useImageSourceProps({
 *   source: 'https://example.com/animated-image.png',
 *   staticSourceUrl: 'https://example.com/static-image.png',
 * });
 *
 * <Image
 *   {...imageSourceProps}
 * />
 */
export const useImageSourceProps: UseImageSourceProps = (params) => {
  const { source, staticSourceUrl } = params;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      source,
      dataSet: staticSourceUrl ? getStaticSourceUrlProps(staticSourceUrl) : undefined,
    }),
    [source, staticSourceUrl],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStaticSourceUrlProps(url: string) {
  return { staticSource: url };
}
